import * as React from 'react';
import { useState, useEffect, Suspense } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';
import axiosInstance from './config/axios'; // dein axios
import { AuthProvider } from './context/AuthContext';
import { DialogProvider } from './context/DialogContext';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';
import { UserRoleProvider } from './context/UserRoleContext';
import { BillingProvider } from './context/BillingContext';
import env from './config/env';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Glass Theme
import './styles/glass-theme.css';
import './styles/glass-components.css';

import Sidebar from './Sidebar';
import ProtectedRoute from './components/ProtectedRoute';
import AdminOnlyRoute from './components/AdminOnlyRoute';
import LocalStorageService from './core/services/LocalStorageService';
import QuickAddButton from './components/QuickAddButton';
import TrialBanner from './components/TrialBanner';

// Haupt-Inhalte
import Employees from './features/employees/components/Employees';
import EmployeeDetail from './features/employees/components/EmployeeDetail';
import Recruiting from './features/employees/screens/Recruiting';
import ApplicantDetail from './features/recruiting/components/ApplicantDetail';
import RecruitingSettings from './features/recruiting/components/RecruitingSettings';
import WeeklyShifts from './features/shifts/components/WeeklyShifts';
import { TemplatesAdminPage } from './features/templates/components/TemplatesAdminPage';
import ChangeRequestReview from './features/timetracking/screens/ChangeRequestReview';
import AdminRatesScreen from './features/earnings/screens/AdminRatesScreen';
// Locations
import LocationList from './LocationList';
import LocationCreate from './LocationCreate';
import LocationEdit from './LocationEdit';

// Dashboard
import Dashboard from './features/dashboard/components/Dashboard';

// Auth/Onboarding
import Login from './features/auth/components/Login';
import SignUp from './features/auth/components/SignUp';

// WorkAreas
import WorkAreaList from './WorkAreaList';
import WorkAreaCreate from './WorkAreaCreate';
import WorkAreaEdit from './WorkAreaEdit';
import WorkAreaDetail from './WorkAreaDetail';

import AdminOverview from './features/admin/components/AdminOverview';
import IntegrationAdminPage from './features/admin/components/IntegrationAdminPage';
import Absences from './features/absences/components/Absences';
import WorkingAreas from './features/workareas/components/WorkingAreas';


// Settings & Integrations
import Integrations from './features/settings/integrations/components/Integrations';
import DatevIntegration from './features/settings/integrations/components/DatevIntegration';
import GoogleCalendarIntegration from './features/settings/integrations/components/GoogleCalendarIntegration';
import OutlookCalendarIntegration from './features/settings/integrations/components/OutlookCalendarIntegration';
import GoogleBusinessIntegration from './features/settings/integrations/components/GoogleBusinessIntegration';
import GoogleBusinessReviews from './features/settings/integrations/components/GoogleBusinessReviews';
import LightspeedIntegration from './features/settings/integrations/components/LightspeedIntegration';
import BankAccounts from './features/settings/bankaccounts/components/BankAccounts';
import BillingCockpit from './features/billing/components/BillingCockpit';
import CompanySettingsPage from './features/settings/company/components/CompanySettingsPage';

import PlatformAdminPage from './features/platform/components/PlatformAdminPage';

// Inventory & Orders
import InventoryPage from './features/inventory/components/InventoryPage';
import OrdersPage from './features/orders/components/OrdersPage';

// Organization
import OrganizationPage from './features/organization/components/OrganizationPage';

// Contacts
import ContactsPage from './features/contacts/components/ContactsPage';
import ContactDetailPage from './features/contacts/components/ContactDetailPage';
import ProjectDetailPage from './features/contacts/components/ProjectDetailPage';

// Sales / Offers / Invoices
import SalesPage from './features/sales/components/SalesPage';
import OfferEditorPage from './features/sales/components/OfferEditorPage';
import OfferDetailPage from './features/sales/components/OfferDetailPage';
import InvoiceDetailPage from './features/sales/components/InvoiceDetailPage';

// Operations / Plantafel
import OperationsPage from './features/operations/components/OperationsPage';

// Events
import EventsCalendar from './features/events/components/EventsCalendar';

// Lazy components must be declared after all import declarations to satisfy import/first
const Payrolls = React.lazy(() => import('./features/employees/screens/Payrolls'));

/**
 * Interface for tenant creation API response
 */
interface TenantCreateResponse {
  id: number;
  name: string;
  description?: string;
  [key: string]: any; // Allow for additional properties that might come from the API
}

/**
 * Interface for API error response
 */
interface ApiError {
  response?: {
    data?: {
      detail?: string;
    };
  };
}

const DISABLED_ROUTES: string[] = [];

// Beispiel-Dummy-Komponenten

function TimeTrackings(): React.JSX.Element {
  return (
    <div style={{ padding: '10px' }}>
      <h2>Time Trackings</h2>
      <p>(Hier könnte deine Time-Tracking-Logik stehen)</p>
    </div>
  );
}

function Availabilities(): React.JSX.Element {
  return (
    <div style={{ padding: '10px' }}>
      <h2>Availabilities</h2>
      <p>(Hier könnte deine Availabilities-Logik stehen)</p>
    </div>
  );
}

/**
 * Home component - renders the Dashboard
 */
function Home(): React.JSX.Element {
  return <Dashboard />;
}

/**
 * CreateTenant component for users to create their first tenant
 */
function CreateTenant(): React.JSX.Element {
  const [tenantName, setTenantName] = useState<string>('');
  const [tenantDescription, setTenantDescription] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    if (!tenantName.trim()) {
      setError('Please enter a tenant name');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    setSuccess('');
    
    try {
      const token = LocalStorageService.getAccessToken();
      if (!token) {
        setError('You must be logged in to create a tenant');
        setIsSubmitting(false);
        return;
      }
      
      await axiosInstance.post<TenantCreateResponse>('/api/tenants/', {
        name: tenantName,
        description: tenantDescription
      });
      
      setSuccess('Tenant created successfully! Redirecting...');
      
      // Force a small delay to ensure the tenant is created
      setTimeout(() => {
        window.location.href = '/home';
      }, 1500);
      
    } catch (err) {
      console.error('Error creating tenant:', err);
      const typedErr = err as ApiError;
      const detail = typedErr.response?.data?.detail || "unknown error";
      setError(`Failed to create tenant: ${detail}`);
      setIsSubmitting(false);
    }
  };

  const handleTenantNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setTenantName(e.target.value);
  };

  const handleTenantDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setTenantDescription(e.target.value);
  };
  
  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
      <h2>Create Your First Tenant</h2>
      <p>You need to create a tenant to continue using the application.</p>

      {success && (
        <div
          style={{
            marginTop: '10px',
            padding: '10px',
            backgroundColor: '#d4edda',
            color: '#155724',
            borderRadius: '4px'
          }}
        >
          {success}
        </div>
      )}

      {error && (
        <div
          style={{
            marginTop: '10px',
            padding: '10px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px'
          }}
        >
          {error}
        </div>
      )}
      
      <div
        style={{
          marginTop: '20px',
          padding: '20px',
          border: '1px solid #ddd',
          borderRadius: '5px'
        }}
      >
        <h3>Tenant Details</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Tenant Name
            </label>
            <input
              type="text"
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
              placeholder="Enter tenant name"
              value={tenantName}
              onChange={handleTenantNameChange}
              disabled={isSubmitting}
              data-testid="tenant-name"
            />
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Tenant Description (Optional)
            </label>
            <textarea
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                minHeight: '100px'
              }}
              placeholder="Describe your tenant"
              value={tenantDescription}
              onChange={handleTenantDescriptionChange}
              disabled={isSubmitting}
            />
          </div>
          
          <button
            type="submit"
            style={{
              background: '#4CAF50',
              color: 'white',
              border: 'none',
              padding: '10px 15px',
              borderRadius: '4px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              opacity: isSubmitting ? 0.7 : 1
            }}
            disabled={isSubmitting}
            data-testid="create-tenant-button"
          >
            {isSubmitting ? 'Creating...' : 'Create Tenant'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Custom sidebar wrapper that checks current route
function SidebarWrapper(): React.JSX.Element | null {
  const location = useLocation();
  const authExcludedPaths = ['/login', '/create-tenant'];

  // Don't show sidebar on login, sign-up, or create-tenant pages
  if (authExcludedPaths.includes(location.pathname)) {
    return null;
  }

  return (
    <ProtectedRoute>
      <div className="glass-sidebar" style={{ zIndex: 10 }}>
        <Sidebar />
      </div>
    </ProtectedRoute>
  );
}

// Quick Add button wrapper that checks current route
function QuickAddWrapper(): React.JSX.Element | null {
  const location = useLocation();
  const authExcludedPaths = ['/login', '/create-tenant'];

  // Don't show quick add button on auth pages
  if (authExcludedPaths.includes(location.pathname)) {
    return null;
  }

  return (
    <ProtectedRoute>
      <QuickAddButton />
    </ProtectedRoute>
  );
}

function TrialBannerWrapper(): React.JSX.Element | null {
  const location = useLocation();
  const excludedPaths = ['/login', '/create-tenant', '/settings/billing'];

  if (excludedPaths.includes(location.pathname)) {
    return null;
  }

  return (
    <ProtectedRoute>
      <TrialBanner />
    </ProtectedRoute>
  );
}

// -------------------
// Inventory Nested
// -------------------
function App(): React.JSX.Element {
  return (
    <AuthProvider>
      <DialogProvider>
      <UserRoleProvider>
      <BillingProvider>
      <BrowserRouter>
        <div
          className="glass-container"
          style={{
            display: 'flex',
            height: '100vh',
            margin: 0,
            padding: 0,
            background: 'var(--glass-gradient-bg)',
          }}
        >
          {/* Animated Background Blobs */}
          <div className="glass-blob glass-blob-1" />
          <div className="glass-blob glass-blob-2" />
          <div className="glass-blob glass-blob-3" />

          {/* Sidebar, nur bei geschützten Routen */}
          <Routes>
            <Route path="*" element={<SidebarWrapper />} />
          </Routes>

          {/* Global Quick Add Button */}
          <Routes>
            <Route path="*" element={<QuickAddWrapper />} />
          </Routes>

          {/* Haupt-Bereich */}
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              position: 'relative',
              zIndex: 1,
              paddingBottom: 40,
            }}
            data-testid="main-content-scrollable"
          >
            <Routes>
              {/* Start/Home */}
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="/home" element={<Home />} />

              {/* Haupt-Funktionen */}
              <Route path="/employees"   element={<Employees />} />
              <Route path="/employees/recruiting" element={<Recruiting />} />
              <Route path="/employees/recruiting/settings" element={<RecruitingSettings />} />
              <Route path="/employees/recruiting/:id" element={<ApplicantDetail />} />
              <Route path="/employees/:id" element={<EmployeeDetail />} />
              <Route path="/employees/documents" element={<AdminOnlyRoute><TemplatesAdminPage /></AdminOnlyRoute>} />
              <Route path="/employees/payrolls" element={<Suspense fallback={<div>Loading...</div>}><AdminOnlyRoute><Payrolls /></AdminOnlyRoute></Suspense>} />
              <Route path="/shiftplan"   element={<WeeklyShifts />} />
              <Route path="/events"      element={<AdminOnlyRoute><EventsCalendar /></AdminOnlyRoute>} />

              {/* WorkAreas */}
              <Route path="/working-areas"      element={<Navigate to="/organization?tab=work-areas" replace />} />
              <Route path="/working-areas/new"  element={<WorkAreaCreate />} />
              <Route path="/working-areas/:id/edit" element={<WorkAreaEdit />} />
              <Route path="/working-areas/:id" element={<WorkAreaDetail />} />

              {/* Locations */}
              <Route path="/locations"          element={<LocationList />} />
              <Route path="/locations/new"      element={<LocationCreate />} />
              <Route path="/locations/:id/edit" element={<LocationEdit />} />

              {/* Operations (Overview, Tasks, Inventory, Orders, HACCP, Plantafel) */}
              <Route path="/operations"  element={DISABLED_ROUTES.includes('/operations') ? <Navigate to="/home" replace /> : <ProtectedRoute><OperationsPage /></ProtectedRoute>} />

              {/* Legacy redirects */}
              <Route path="/inventory/*" element={<Navigate to="/operations?tab=inventory" replace />} />
              <Route path="/orders" element={<Navigate to="/operations?tab=orders" replace />} />
              <Route path="/haccp" element={<Navigate to="/operations?tab=haccp" replace />} />
              <Route path="/reviews" element={DISABLED_ROUTES.includes('/reviews') ? <Navigate to="/home" replace /> : <ProtectedRoute><GoogleBusinessReviews /></ProtectedRoute>} />
              <Route path="/tickets" element={DISABLED_ROUTES.includes('/tickets') ? <Navigate to="/home" replace /> : <div>Tickets Screen</div>} />

              {/* Inventory & Orders */}
              <Route path="/funktionen/inventur" element={<ProtectedRoute><InventoryPage /></ProtectedRoute>} />
              <Route path="/funktionen/bestellungen" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />

              {/* Admin Routes */}
              <Route
                path="/admin-overview"
                element={
                  <ProtectedRoute>
                    <AdminOverview />
                  </ProtectedRoute>
                }
              />

              {/* Settings & Integrations */}
              <Route
                path="/settings/integrations"
                element={DISABLED_ROUTES.includes('/settings/integrations') ? <Navigate to="/home" replace /> :
                  <ProtectedRoute>
                    <Integrations />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/integrations/datev"
                element={DISABLED_ROUTES.includes('/settings/integrations') ? <Navigate to="/home" replace /> :
                  <ProtectedRoute>
                    <DatevIntegration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/integrations/google-calendar"
                element={DISABLED_ROUTES.includes('/settings/integrations') ? <Navigate to="/home" replace /> :
                  <ProtectedRoute>
                    <GoogleCalendarIntegration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/integrations/outlook-calendar"
                element={DISABLED_ROUTES.includes('/settings/integrations') ? <Navigate to="/home" replace /> :
                  <ProtectedRoute>
                    <OutlookCalendarIntegration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/integrations/google-business"
                element={
                  <ProtectedRoute>
                    <GoogleBusinessIntegration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/integrations/lightspeed"
                element={
                  <ProtectedRoute>
                    <LightspeedIntegration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/google-reviews"
                element={<Navigate to="/reviews" replace />}
              />
              <Route
                path="/settings/bank-accounts"
                element={
                  <ProtectedRoute>
                    <BankAccounts />
                  </ProtectedRoute>
                }
              />
              {/* Billing */}
              <Route
                path="/settings/billing"
                element={
                  <ProtectedRoute>
                    <BillingCockpit />
                  </ProtectedRoute>
                }
              />
              {/* Organization */}
              <Route
                path="/organization"
                element={
                  <ProtectedRoute>
                    <OrganizationPage />
                  </ProtectedRoute>
                }
              />

              {/* Legacy route - redirect to organization */}
              <Route
                path="/settings/company"
                element={<Navigate to="/organization?tab=company" replace />}
              />

              {/* Contacts */}
              <Route
                path="/contacts"
                element={DISABLED_ROUTES.includes('/contacts') ? <Navigate to="/home" replace /> :
                  <ProtectedRoute>
                    <ContactsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/contacts/:id"
                element={DISABLED_ROUTES.includes('/contacts') ? <Navigate to="/home" replace /> :
                  <ProtectedRoute>
                    <ContactDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId"
                element={DISABLED_ROUTES.includes('/contacts') ? <Navigate to="/home" replace /> :
                  <ProtectedRoute>
                    <ProjectDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* Sales */}
              <Route
                path="/sales"
                element={DISABLED_ROUTES.includes('/sales') ? <Navigate to="/home" replace /> :
                  <ProtectedRoute>
                    <SalesPage />
                  </ProtectedRoute>
                }
              />

              {/* Offers */}
              <Route
                path="/offers/new"
                element={DISABLED_ROUTES.includes('/sales') ? <Navigate to="/home" replace /> :
                  <ProtectedRoute>
                    <OfferEditorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/offers/:offerId"
                element={DISABLED_ROUTES.includes('/sales') ? <Navigate to="/home" replace /> :
                  <ProtectedRoute>
                    <OfferDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/offers/:offerId/edit"
                element={DISABLED_ROUTES.includes('/sales') ? <Navigate to="/home" replace /> :
                  <ProtectedRoute>
                    <OfferEditorPage />
                  </ProtectedRoute>
                }
              />

              {/* Invoices */}
              <Route
                path="/invoices/:invoiceId"
                element={DISABLED_ROUTES.includes('/sales') ? <Navigate to="/home" replace /> :
                  <ProtectedRoute>
                    <InvoiceDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* Zusätzliche Routen */}
              <Route path="/absences"       element={<Absences />} />
              <Route path="/time-trackings" element={<TimeTrackings />} />
              <Route path="/availabilities" element={<Availabilities />} />
              <Route path="/time-trackings/change-requests/:id/review" element={<ProtectedRoute><ChangeRequestReview /></ProtectedRoute>} />
              <Route path="/admin/earnings/rates" element={<ProtectedRoute><AdminRatesScreen /></ProtectedRoute>} />
              <Route path="/admin/integrations" element={<AdminOnlyRoute><IntegrationAdminPage /></AdminOnlyRoute>} />

              {/* Platform Admin */}
              <Route
                path="/platform"
                element={
                  <ProtectedRoute>
                    <PlatformAdminPage />
                  </ProtectedRoute>
                }
              />

              {/* Auth */}
              <Route path="/login"   element={<Login />} />
              <Route path="/sign-up" element={<Navigate to="/login" replace />} />

              <Route
                path="/create-tenant"
                element={
                  <ProtectedRoute>
                    <CreateTenant />
                  </ProtectedRoute>
                }
              />

              {/* Fallback */}
              <Route
                path="*"
                element={
                  <ProtectedRoute>
                    <div>404 – Seite nicht gefunden</div>
                  </ProtectedRoute>
                }
              />
            </Routes>

            {/* Trial Banner — sticky footer inside scroll container */}
            <Routes>
              <Route path="*" element={<TrialBannerWrapper />} />
            </Routes>
          </div>
        </div>
        <SessionTimeoutWarning />
        <ToastContainer hideProgressBar />
      </BrowserRouter>
      </BillingProvider>
      </UserRoleProvider>
      </DialogProvider>
    </AuthProvider>
  );
}

export default App; 
